import { Button, TagField, Text } from '@viapeople/components'
import { useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { AddHierarchiesLevelModal } from './AddHierarchiesLevelModal'
import { AddUsersModal } from './AddUsersModal'

export interface PopulationInputProjection {
	title?: boolean
	rolesTitle?: boolean
	roles?: boolean
	hierarchyTitle?: boolean
	hierarchyLevels?: boolean
	blacklistedHierarchyRoles?: boolean
	reviewersTitle?: boolean
	blacklistedUsers?: boolean
	whitelistedUsers?: boolean
}

export interface PopulationFieldsProps {
	name: string
	projection?: PopulationInputProjection
	cycleId: string
	organizationId: string
	disabled?: boolean
}

const Projected = ({ visible, children }: { visible?: boolean; children: React.ReactNode }) => {
	if (visible === false) return null
	return children
}

export const PopulationFields = ({
	name,
	projection,
	cycleId: _cycleId,
	organizationId,
	disabled,
}: PopulationFieldsProps) => {
	const { setValue, getValues } = useFormContext()
	const [pickingHierarchies, setPickingHierarchies] = useState(false)
	const [pickingUsers, setPickingUsers] = useState<'whitelistedUsers' | 'blacklistedUsers'>()
	const getName = (propName: string) => `${name}.${propName}`

	return (
		<div className='flex-1 flex flex-col gap-4'>
			<Projected visible={projection?.title}>
				<Text variant='medium' weight='semibold' className='text-left'>
					Define Population
				</Text>
			</Projected>
			<Projected visible={projection?.hierarchyTitle}>
				<Text variant='small' weight='semibold' className='text-left'>
					By hierarchy
				</Text>
			</Projected>
			<div className='flex-1 flex flex-row gap-4'>
				<Projected visible={projection?.hierarchyLevels}>
					<div className='flex-1 flex flex-row gap-2 items-end'>
						<Controller
							name={getName('hierarchyLevels')}
							render={({ field }) => (
								<TagField
									label='Add Hierarchies'
									placeholder='Add Hierarchies'
									{...field}
									disabled={disabled}
									value={field.value?.map((v: number) => ({
										id: v.toString(),
										label: `Level ${v}`,
									}))}
									onChange={(hirarchies) =>
										setValue(
											getName('hierarchyLevels'),
											hirarchies.map((h) => Number(h.id)),
											{ shouldDirty: true },
										)
									}
								/>
							)}
						/>

						<Button
							variant='secondary'
							onClick={(e) => {
								e.preventDefault()
								setPickingHierarchies(true)
							}}
							disabled={disabled}
							className={disabled ? 'opacity-50' : ''}>
							Add hierarchies
						</Button>
					</div>
				</Projected>

				<Projected visible={projection?.blacklistedHierarchyRoles}>
					<div className='flex-1 flex flex-row gap-2 items-end'>
						<Controller
							name={getName('blacklistedHierarchyRoles')}
							render={({ field }) => (
								<TagField
									label='Exclude from hierarchies by role'
									placeholder='Add Roles'
									{...field}
									disabled={disabled}
									onChange={(roles) => setValue(getName('blacklistedHierarchyRoles'), roles, { shouldDirty: true })}
								/>
							)}
						/>

						{/* TODO: Implement Add Roles button */}
					</div>
				</Projected>
			</div>

			<Projected visible={projection?.rolesTitle}>
				<Text variant='small' weight='semibold' className='text-left'>
					By role
				</Text>
			</Projected>

			<Projected visible={projection?.roles}>
				<div className='flex-1 flex flex-row gap-2 items-end'>
					<Controller
						name={getName('roles')}
						render={({ field }) => (
							<TagField
								label='By role'
								placeholder='Add Roles'
								{...field}
								disabled={disabled}
								onChange={(roles) => setValue(getName('roles'), roles, { shouldDirty: true })}
							/>
						)}
					/>

					{/* TODO: Implement Add Roles button */}
				</div>
			</Projected>

			<Projected visible={projection?.reviewersTitle}>
				<Text variant='small' weight='semibold' className='text-left'>
					By specific users
				</Text>
			</Projected>
			<div className='flex-1 flex flex-row gap-4'>
				<Projected visible={projection?.whitelistedUsers}>
					<div className='flex-1 flex flex-row gap-2 items-end'>
						<Controller
							name={getName('whitelistedUsers')}
							render={({ field }) => (
								<TagField
									label='Add users'
									placeholder='Ad hoc users'
									{...field}
									disabled={disabled}
									onChange={(reviewers) => setValue(getName('whitelistedUsers'), reviewers, { shouldDirty: true })}
								/>
							)}
						/>

						<Button
							variant='secondary'
							onClick={(e) => {
								e.preventDefault()
								setPickingUsers('whitelistedUsers')
							}}
							disabled={disabled}
							className={disabled ? 'opacity-50' : ''}>
							Add users
						</Button>
					</div>
				</Projected>

				<Projected visible={projection?.blacklistedUsers}>
					<div className='flex-1 flex flex-row gap-2 items-end'>
						<Controller
							name={getName('blacklistedUsers')}
							render={({ field }) => (
								<TagField
									label='Exclude users'
									placeholder='Ad hoc users'
									{...field}
									disabled={disabled}
									onChange={(reviewers) => setValue(getName('blacklistedUsers'), reviewers, { shouldDirty: true })}
								/>
							)}
						/>

						<Button
							variant='secondary'
							onClick={(e) => {
								e.preventDefault()
								setPickingUsers('blacklistedUsers')
							}}
							disabled={disabled}
							className={disabled ? 'opacity-50' : ''}>
							Add users
						</Button>
					</div>
				</Projected>
			</div>

			<AddHierarchiesLevelModal
				isOpen={pickingHierarchies}
				onClose={() => setPickingHierarchies(false)}
				currentValues={() => getValues(getName('hierarchyLevels')) ?? []}
				onSelect={(levels) => {
					setValue(getName('hierarchyLevels'), levels, { shouldDirty: true })
					setPickingHierarchies(false)
				}}
			/>

			<AddUsersModal
				isOpen={!!pickingUsers}
				onClose={() => setPickingUsers(undefined)}
				organizationId={organizationId}
				title={pickingUsers === 'whitelistedUsers' ? 'Add Users' : 'Exclude Users'}
				excludedUserExternalIds={() =>
					new Set((getValues(getName(pickingUsers as string)) ?? []).map((r: { externalId: string }) => r.externalId))
				}
				onSelect={(users) => {
					setValue(
						getName(pickingUsers as string),
						(getValues(getName(pickingUsers as string)) ?? []).concat(
							users.map((u) => ({
								id: u.id,
								externalId: u.externalId,
								label: `${u.firstName} ${u.lastName}`,
							})),
						),
						{ shouldDirty: true },
					)
					setPickingUsers(undefined)
				}}
			/>
		</div>
	)
}
